import { Menu } from '@mantine/core'
import type { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { Button, cn, KBD, TypographyDetail } from '@seen/ui'
import {
  IconArrowLeft,
  IconArrowRight,
  IconFileUpload,
  IconMessageChatbot,
  IconPlus,
  IconReload,
  IconSearch,
} from '@tabler/icons-react'
import { type JSX, type ReactNode, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { HeaderDropdown } from '#src/components/app/header/HeaderDropdown.tsx'
import { NavSheetButton } from '#src/components/app/header/NavSheetButton.tsx'
import DailyAppointmentsIsland from '#src/components/appointment/DailyAppointmentsIsland.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { DropdownMenu } from '#src/components/core/dropdown-menu/DropdownMenu.tsx'
import { DropdownMenuContent } from '#src/components/core/dropdown-menu/DropdownMenuContent.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { DropdownMenuTrigger } from '#src/components/core/dropdown-menu/DropdownMenuTrigger.tsx'
import { Sheet } from '#src/components/core/sheet/Sheet.tsx'
import { SheetContent } from '#src/components/core/sheet/SheetContent.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { SendFaxModal } from '#src/components/fax/SendFaxModal.tsx'
import { IconFax } from '#src/components/icons/IconFax.tsx'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { IconNewTimelineNote } from '#src/components/icons/IconNewTimelineNote.tsx'
import { IconOrder } from '#src/components/icons/IconOrder.tsx'
import { OrderForm } from '#src/components/order/order-form/OrderForm.tsx'
import { OrderCreatedToast } from '#src/components/order/OrderCreatedToast.tsx'
import { TimelineNoteCreatedToast } from '#src/components/TimelineNoteComposerDialog.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import {
  useDemoMode,
  useDemoModeFlagGuard,
} from '#src/demo-mode/useDemoMode.ts'
import { useAIAssistantStore } from '#src/hooks/useAIAssistantStore.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useCurrentPractitionerPerms } from '#src/hooks/useCurrentPractitionerPerms.ts'
import { useGlobalFileUpload } from '#src/hooks/useGlobalFileUpload.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useTaskComposerStore } from '#src/hooks/useTaskComposerStore.ts'
import { useTimelineNoteComposerDialogStore } from '#src/hooks/useTimelineNoteComposerDialogStore.ts'
import { useWebSocketConnectionStatus } from '#src/hooks/useWebSocketConnectionStatus.ts'

export interface HeaderProps {
  readonly notifications?: ReactNode
  readonly isStaging?: boolean
  /** App-supplied account-menu actions, injected into the avatar dropdown. */
  readonly accountMenuItems?: ReactNode
}

export function Header({
  isStaging,
  accountMenuItems,
}: HeaderProps): JSX.Element {
  const navigate = useNavigate()
  const practitioner = useCurrentPractitioner()
  const [userMenuOpened, setUserMenuOpened] = useState(false)
  const setCommandMenuOpen = usePortalStore((state) => state.setCommandMenuOpen)
  const setTaskComposerOpen = useTaskComposerStore((state) => state.setOpen)
  const setTimelineNoteComposerDialogOpen = useTimelineNoteComposerDialogStore(
    (state) => state.setOpen
  )
  const { appVersion, appCommitSha, aiEnabled } = useSeenApi()
  const { showUploadArea } = useGlobalFileUpload()
  const isReadOnly = useIsReadOnly()
  const { isAuditor } = useCurrentPractitionerPerms()
  const assistantOpen = useAIAssistantStore((state) => state.open)
  const toggleAssistant = useAIAssistantStore((state) => state.toggleOpen)
  const { status: webSocketStatus } = useWebSocketConnectionStatus()
  const demoMode = useDemoMode()
  useDemoModeFlagGuard()
  const showDemoBadge = isStaging || demoMode

  const [showOrderComposer, setShowOrderComposer] = useState(false)
  const [showSendFaxModal, setShowSendFaxModal] = useState(false)
  useHotkeys(Hotkeys.OrderComposer, () => setShowOrderComposer(true), {
    enabled: !isReadOnly,
  })

  useHotkeys(Hotkeys.UploadFiles, showUploadArea, {
    enabled: !isReadOnly,
  })

  return (
    <header className="flex items-center justify-between print:hidden">
      <div className="flex items-center">
        <NavSheetButton />
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="rounded-none hover:bg-frame-hover hover:backdrop-brightness-100"
            >
              <IconArrowLeft className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-2 px-2">
            <TypographyDetail className="text-muted-foreground">
              Go back
            </TypographyDetail>
            <KBD>Esc</KBD>
          </TooltipContent>
        </Tooltip>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(1)}
          className="rounded-none hover:bg-frame-hover hover:backdrop-brightness-100"
        >
          <IconArrowRight className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => window.location.reload()}
          className="rounded-none hover:bg-frame-hover hover:backdrop-brightness-100"
        >
          <IconReload className="size-4" />
        </Button>
      </div>
      <div className="flex items-center">
        <DailyAppointmentsIsland />
        {!isReadOnly && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-none hover:bg-frame-hover hover:backdrop-brightness-100"
                aria-label="New…"
              >
                <IconPlus className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onSelect={() => setTaskComposerOpen(true)}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <IconNewTask />
                  New task
                </div>
                <KBD>T</KBD>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setTimelineNoteComposerDialogOpen(true, {
                    onSuccess: (comment) => {
                      toast.success(
                        <TimelineNoteCreatedToast comment={comment} />
                      )
                    },
                  })
                }}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <IconNewTimelineNote />
                  New chart note
                </div>
                <KBD>N</KBD>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => setShowOrderComposer(true)}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <IconOrder />
                  New order
                </div>
                <KBD>O</KBD>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={showUploadArea}
                className="justify-between"
              >
                <div className="flex items-center gap-2">
                  <IconFileUpload className="size-4" />
                  New file upload
                </div>
                <div className="flex gap-1">
                  <KBD>ctrl</KBD>
                  <KBD>shift</KBD>
                  <KBD>U</KBD>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShowSendFaxModal(true)}>
                <div className="flex items-center gap-2">
                  <IconFax />
                  Send fax
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Tooltip delayDuration={500}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCommandMenuOpen(true)}
              className="rounded-none hover:bg-frame-hover hover:backdrop-brightness-100"
            >
              <IconSearch className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="flex items-center gap-2 px-2">
            <TypographyDetail className="text-muted-foreground">
              Toggle command menu
            </TypographyDetail>
            <div className="flex gap-1">
              <KBD>⌘</KBD>
              <KBD>K</KBD>
            </div>
          </TooltipContent>
        </Tooltip>
        {aiEnabled && !isAuditor && (
          <Tooltip delayDuration={500}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleAssistant}
                className={cn(
                  'rounded-none hover:bg-frame-hover hover:backdrop-brightness-100',
                  assistantOpen && 'bg-frame-hover'
                )}
              >
                <IconMessageChatbot className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent className="flex items-center gap-2 px-2">
              <TypographyDetail className="text-muted-foreground">
                Orbit AI
              </TypographyDetail>
              <div className="flex gap-1">
                <KBD>⌘</KBD>
                <KBD>⇧</KBD>
                <KBD>A</KBD>
              </div>
            </TooltipContent>
          </Tooltip>
        )}
        <Menu
          width={260}
          shadow="xl"
          position="bottom-end"
          transitionProps={{ transition: 'pop-top-right' }}
          opened={userMenuOpened}
          onClose={() => setUserMenuOpened(false)}
        >
          <Menu.Target>
            <Button
              variant="ghost"
              onClick={() => setUserMenuOpened((o) => !o)}
              className="relative size-auto rounded-none px-2 hover:bg-frame-hover hover:backdrop-brightness-100"
            >
              {showDemoBadge && <DemoAvatarBadge practitioner={practitioner} />}
              {!showDemoBadge && (
                <Avatar
                  profile={practitioner.toFhirResource()}
                  size="icon"
                  className="mr-1.5 ml-0.5"
                />
              )}
              {!showDemoBadge && (
                <span
                  className={cn(
                    'absolute top-1 right-1.5 size-2 rounded-full ring-1 ring-background',
                    webSocketStatus === 'connected' && 'bg-emerald-500',
                    webSocketStatus === 'connecting' && 'bg-amber-400',
                    webSocketStatus === 'paused' && 'bg-slate-400',
                    webSocketStatus === 'disconnected' && 'bg-red-500'
                  )}
                  title={`WebSocket: ${webSocketStatus}`}
                />
              )}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <HeaderDropdown
              isStaging={isStaging}
              accountMenuItems={accountMenuItems}
            >
              {appVersion && appCommitSha && (
                <div className="block text-center text-xs text-muted-foreground">
                  version: {appVersion}
                  <br />
                  commitSha: {appCommitSha.slice(0, 7)}
                </div>
              )}
            </HeaderDropdown>
          </Menu.Dropdown>
        </Menu>
      </div>
      {!isReadOnly && (
        <Sheet open={showOrderComposer} onOpenChange={setShowOrderComposer}>
          <SheetContent hideClose side="right" className="max-w-2xl">
            <OrderForm
              onCancel={() => setShowOrderComposer(false)}
              onSubmit={(newOrder, options) => {
                setShowOrderComposer(false)
                // Skip the generic "Order created" toast for deferred (async)
                // submissions — LabSubmissionWatcher surfaces the terminal
                // toast once the subscription handler completes.
                if (!options?.deferred) {
                  toast.success(<OrderCreatedToast order={newOrder} />)
                }
              }}
            />
          </SheetContent>
        </Sheet>
      )}
      {!isReadOnly && (
        <SendFaxModal
          open={showSendFaxModal}
          onOpenChange={setShowSendFaxModal}
        />
      )}
    </header>
  )
}

function DemoAvatarBadge({
  practitioner,
}: {
  practitioner: ExtendedPractitioner
}): JSX.Element {
  return (
    <div className="flex items-center gap-1 rounded-full border p-0.5">
      <span className="pl-1.5 text-3xs text-frame-foreground-secondary">
        DEMO
      </span>
      <Avatar profile={practitioner.toFhirResource()} size="icon" />
    </div>
  )
}
